import { useCancel, useDiscard, useResolve, useRetry } from '@/hooks/useErrorHandlingSagas'
import { Button, type ButtonProps } from '@chakra-ui/react'
import { SagaAssigneeSelect } from './SagaAssigneeSelect'

type SagaActionsProps = {
  ids: string[]
  onDone: () => void | Promise<void>
  isDisabled?: boolean
  size?: ButtonProps['size']
}

// Přiřadit / Znovu provolat / Vyřešit / Vyřadit / Zrušit — shared by the list (bulk over selected ids) and the detail (single id).
export const SagaActions = ({ ids, onDone, isDisabled, size }: SagaActionsProps) => {
  const retry = useRetry()
  const resolve = useResolve()
  const discard = useDiscard()
  const cancel = useCancel()

  const busy = retry.isPending || resolve.isPending || discard.isPending || cancel.isPending
  const disabled = isDisabled || busy || ids.length === 0

  const run = async (fn: () => Promise<unknown>) => {
    await fn()
    await onDone()
  }

  const onDiscard = () => {
    const reason = window.prompt('Důvod vyřazení (povinný):')?.trim()
    if (!reason) return
    run(() => discard.mutateAsync({ ids, reason }))
  }

  const onCancel = () => {
    // Single dialog = confirm gate + optional reason. Dialog Cancel (null) aborts; empty text → no reason.
    const reason = window.prompt('Zrušit vybrané zpracování? Volitelně uveďte důvod a potvrďte OK:')
    if (reason === null) return
    run(() => cancel.mutateAsync({ ids, reason: reason.trim() || undefined }))
  }

  return (
    <>
      <SagaAssigneeSelect ids={ids} onAssigned={onDone} isDisabled={disabled} restPlaceholder="Přiřadit požadavky" />
      <Button colorScheme="green" size={size} isDisabled={disabled} onClick={() => run(() => retry.mutateAsync({ ids }))}>Znovu provolat</Button>
      <Button colorScheme="teal" size={size} isDisabled={disabled} onClick={() => run(() => resolve.mutateAsync({ ids }))}>Vyřešit</Button>
      <Button colorScheme="red" size={size} isDisabled={disabled} onClick={onDiscard}>Vyřadit</Button>
      <Button colorScheme="orange" size={size} isDisabled={disabled} onClick={onCancel}>Zrušit</Button>
    </>
  )
}
